import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import { toast } from "sonner";
import {
  ShieldCheck,
  Search,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  X,
  User as UserIcon,
  FileText,
  Camera,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const STATUS_TABS = [
  { key: "pending", label: "Pending review", color: "amber" },
  { key: "verified", label: "Approved", color: "emerald" },
  { key: "rejected", label: "Rejected", color: "rose" },
  { key: "all", label: "All", color: "gray" },
];

const STATUS_PILL = {
  pending: "bg-amber-50 border border-amber-200 text-amber-800",
  verified: "bg-emerald-50 border border-emerald-200 text-emerald-800",
  rejected: "bg-rose-50 border border-rose-200 text-rose-800",
  unverified: "bg-gray-100 border border-gray-200 text-gray-700",
};

const VERDICT_LABEL = {
  verified: { txt: "AI: Approved", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  rejected: { txt: "AI: Rejected", cls: "text-rose-700 bg-rose-50 border-rose-200" },
  manual_review: { txt: "AI: Needs review", cls: "text-amber-700 bg-amber-50 border-amber-200" },
};

// Resolve a token-aware URL for KYC files (so <img src=...> works through interceptor)
const fileUrl = (id, kind) =>
  `${API}/site/admin/kyc/${id}/file/${kind}`;

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s || "—";
  }
};

const Pill = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider ${STATUS_PILL[status] || STATUS_PILL.unverified}`}>
    {status === "pending" && <Clock size={10} />}
    {status === "verified" && <CheckCircle2 size={10} />}
    {status === "rejected" && <XCircle size={10} />}
    {status}
  </span>
);

const ConfBar = ({ value, label }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const color = v >= 80 ? "bg-emerald-500" : v >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
        <span>{label}</span>
        <span className="font-mono font-semibold tabular text-gray-700">{v}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-sm overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

// Authenticated <img> loader using fetch+blob → object URL.
// Required because admin endpoints expect Bearer token (cookies blocked in iframe).
const AuthImg = ({ src, alt, className, onClick }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let revoked = false;
    setUrl(null);
    setErr(false);
    const token = localStorage.getItem("ss_token");
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.blob();
      })
      .then((b) => {
        if (revoked) return;
        const u = URL.createObjectURL(b);
        setUrl(u);
      })
      .catch(() => setErr(true));
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line
  }, [src]);
  if (err) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-gray-400 text-xs ${className}`}>
        <AlertTriangle size={16} className="mr-1.5" /> Cannot load
      </div>
    );
  }
  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 ${className}`}>
        <RefreshCw size={16} className="animate-spin text-gray-400" />
      </div>
    );
  }
  return <img src={url} alt={alt} className={className} onClick={onClick} loading="lazy" />;
};

// Lightbox for full-screen image preview
const Lightbox = ({ images, idx, onClose, onPrev, onNext }) => {
  if (idx == null) return null;
  const cur = images[idx];
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="kyc-lightbox"
    >
      <button
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      >
        <X size={20} />
      </button>
      {idx > 0 && (
        <button
          className="absolute left-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
        >
          <ChevronLeft size={24} />
        </button>
      )}
      {idx < images.length - 1 && (
        <button
          className="absolute right-4 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
        >
          <ChevronRight size={24} />
        </button>
      )}
      <div className="text-center max-h-full max-w-full" onClick={(e) => e.stopPropagation()}>
        <AuthImg src={cur.src} alt={cur.label} className="max-h-[85vh] max-w-[90vw] object-contain rounded-sm shadow-2xl" />
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white/10 text-white text-sm">
          {cur.label} · {idx + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

const ReviewModal = ({ submission, onClose, onDecided }) => {
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [lightIdx, setLightIdx] = useState(null);

  const ai = submission.ai || {};
  const verdict = (ai.verdict || "manual_review").toLowerCase();
  const v = VERDICT_LABEL[verdict] || VERDICT_LABEL.manual_review;
  const decided = !!submission.decision_at;

  const images = useMemo(
    () => [
      { kind: "id_doc_front", label: "ID · Front", src: fileUrl(submission.id, "id_doc_front") },
      { kind: "id_doc_back",  label: "ID · Back",  src: fileUrl(submission.id, "id_doc_back") },
      { kind: "selfie",       label: "Selfie",     src: fileUrl(submission.id, "selfie") },
    ],
    [submission.id]
  );

  const decide = async (decision) => {
    if (decision === "reject" && !reason.trim()) {
      toast.error("Please add a short reason for rejection (player will see it).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/site/admin/kyc/${submission.id}/decision`, {
        decision,
        reason: reason.trim() || undefined,
      });
      toast.success(decision === "approve" ? "Approved ✓ — player notified" : "Rejected — player notified");
      onDecided(data.status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="kyc-review-modal">
      <div className="bg-white rounded-sm w-full max-w-5xl my-4 shadow-2xl border border-gray-200" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-violet-50 border border-violet-200 text-violet-600 flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="font-display text-lg font-bold">KYC Review</div>
              <div className="text-xs text-gray-500 font-mono">
                #{submission.id.slice(0, 8)} · {submission.username || "—"} · {fmtDate(submission.created_at)}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-sm hover:bg-gray-100 flex items-center justify-center text-gray-500" data-testid="kyc-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* LEFT: 3 images */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {images.map((img, i) => (
              <div key={img.kind} className="rounded-sm border border-gray-200 overflow-hidden bg-gray-50">
                <div className="px-3 py-2 bg-white border-b border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-600 flex items-center gap-1">
                    {img.kind === "selfie" ? <Camera size={11} /> : <FileText size={11} />}
                    {img.label}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setLightIdx(i)}
                  className="block w-full aspect-[3/4] bg-gray-100 hover:opacity-90 transition-opacity"
                  data-testid={`kyc-img-${img.kind}`}
                >
                  <AuthImg src={img.src} alt={img.label} className="w-full h-full object-cover cursor-zoom-in" />
                </button>
              </div>
            ))}
          </div>

          {/* RIGHT: AI verdict + account info */}
          <div className="space-y-4">
            <div className={`rounded-sm border p-3 ${v.cls}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1">
                  <Sparkles size={11} /> AI Verdict
                </div>
                <span className="font-mono text-[11px]">
                  {verdict.replace("_", " ")}
                </span>
              </div>
              <div className="text-xs leading-relaxed">{ai.reason || "No AI reason recorded."}</div>
            </div>

            <div className="rounded-sm border border-gray-200 p-3 bg-white">
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Match scores</div>
              <div className="space-y-2.5">
                <ConfBar value={ai.face_match_confidence} label="Face match (selfie ↔ ID)" />
                <ConfBar value={ai.name_match_confidence} label="Name match" />
                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-gray-500">DOB match</span>
                  <span className={`font-semibold ${
                    ai.dob_match === "match" ? "text-emerald-700" :
                    ai.dob_match === "mismatch" ? "text-rose-700" : "text-gray-500"
                  }`}>{ai.dob_match || "unknown"}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Liveness check</span>
                  <span className={`font-semibold ${ai.liveness_ok ? "text-emerald-700" : "text-rose-700"}`}>
                    {ai.liveness_ok ? "Pass" : "Fail / unknown"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Document quality</span>
                  <span className="font-semibold text-gray-800 capitalize">{ai.document_quality || "—"}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Document type</span>
                  <span className="font-semibold text-gray-800 uppercase">{ai.document_type || "—"}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Back present</span>
                  <span className={`font-semibold ${ai.back_present ? "text-emerald-700" : "text-rose-700"}`}>
                    {ai.back_present ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-sm border border-gray-200 p-3 bg-white">
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
                <UserIcon size={11} /> Extracted from document
              </div>
              <div className="text-xs space-y-1">
                <div><span className="text-gray-500">Name:</span> <b className="font-mono">{ai.extracted_name || "—"}</b></div>
                <div><span className="text-gray-500">ID №:</span> <b className="font-mono">{ai.extracted_id_number || "—"}</b></div>
                <div><span className="text-gray-500">DOB:</span> <b className="font-mono">{ai.extracted_dob || "—"}</b></div>
              </div>
            </div>

            {decided && (
              <div className="rounded-sm border border-gray-200 p-3 bg-gray-50 text-xs">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Already decided</div>
                <div className="text-gray-700">
                  <Pill status={submission.status} />
                  <div className="mt-1.5">
                    by <b>{submission.decision_by || "admin"}</b> · {fmtDate(submission.decision_at)}
                  </div>
                  {submission.decision_reason && (
                    <div className="mt-1 italic text-gray-600">"{submission.decision_reason}"</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!decided && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
            {!rejectMode ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                <div className="text-xs text-gray-600">
                  Review the documents and pictures, then decide. The player will receive an instant notification either way.
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRejectMode(true)}
                    disabled={busy}
                    className="h-10 px-4 rounded-sm border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="kyc-btn-reject"
                  >
                    <XCircle size={14} /> Reject
                  </button>
                  <button
                    type="button"
                    onClick={() => decide("approve")}
                    disabled={busy}
                    className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="kyc-btn-approve"
                  >
                    <CheckCircle2 size={14} /> {busy ? "Approving…" : "Approve"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Rejection reason <span className="text-rose-600">*</span> <span className="text-gray-400 font-normal lowercase">(player will see this)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. ID front photo is blurry — please upload a clearer image."
                  className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-rose-500"
                  data-testid="kyc-reject-reason"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setRejectMode(false); setReason(""); }}
                    className="h-10 px-4 rounded-sm border border-gray-200 hover:bg-gray-100 text-sm font-semibold text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => decide("reject")}
                    disabled={busy || !reason.trim()}
                    className="h-10 px-5 bg-rose-500 hover:bg-rose-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="kyc-confirm-reject"
                  >
                    <XCircle size={14} /> {busy ? "Rejecting…" : "Confirm reject"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Lightbox
        images={images}
        idx={lightIdx}
        onClose={() => setLightIdx(null)}
        onPrev={() => setLightIdx((i) => Math.max(0, i - 1))}
        onNext={() => setLightIdx((i) => Math.min(images.length - 1, i + 1))}
      />
    </div>
  );
};

export default function KycReview() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);

  const load = async (statusKey = tab) => {
    setLoading(true);
    try {
      const params = statusKey === "all" ? {} : { status: statusKey };
      const { data } = await api.get("/site/admin/kyc/list", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load KYC submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => {
      const ai = s.ai || {};
      return (
        (s.username || "").toLowerCase().includes(q) ||
        (s.id || "").toLowerCase().includes(q) ||
        (ai.extracted_name || "").toLowerCase().includes(q) ||
        (ai.extracted_id_number || "").toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  const counts = useMemo(() => ({
    pending:  items.filter(x => x.status === "pending").length,
    verified: items.filter(x => x.status === "verified").length,
    rejected: items.filter(x => x.status === "rejected").length,
  }), [items]);

  const paged = usePagedRows(filtered, 12, [tab, search]);

  return (
    <div data-testid="kyc-review-page">
      <PageHeader
        title="KYC Review"
        subtitle="Review documents from players whose AI verification could not auto-decide. Approve or reject — the player is notified instantly."
      />

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-sm p-2 mb-4 flex flex-wrap items-center gap-2" data-testid="kyc-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`kyc-tab-${t.key}`}
            className={`h-9 px-4 rounded-sm text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2 transition-colors ${
              tab === t.key
                ? `bg-${t.color}-500 text-white`
                : "bg-gray-50 hover:bg-gray-100 text-gray-700"
            }`}
            style={tab === t.key ? {
              background: t.color === "amber" ? "#f59e0b" :
                          t.color === "emerald" ? "#10b981" :
                          t.color === "rose" ? "#f43f5e" : "#374151",
              color: "#fff",
            } : {}}
          >
            {t.label}
            {tab === t.key && t.key !== "all" && (
              <span className="bg-white/30 px-1.5 rounded-sm text-[10px] tabular">
                {counts[t.key] ?? 0}
              </span>
            )}
          </button>
        ))}
        <div className="flex-1 min-w-[200px] flex justify-end">
          <button
            type="button"
            onClick={() => load(tab)}
            className="h-9 px-3 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 inline-flex items-center gap-1.5"
            title="Refresh"
            data-testid="kyc-refresh"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border border-gray-200 rounded-sm p-3 mb-3 flex items-center gap-2">
        <Search size={14} className="text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by username, ID number, name…"
          className="flex-1 outline-none text-sm"
          data-testid="kyc-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                <th className="text-left px-4 py-3">Submitted</th>
                <th className="text-left px-4 py-3">Player</th>
                <th className="text-left px-4 py-3">AI verdict</th>
                <th className="text-left px-4 py-3">Face / Name</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">Loading submissions…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                  <ShieldCheck size={28} className="mx-auto text-gray-300 mb-2" />
                  <div className="text-sm font-semibold">No submissions in this tab</div>
                  <div className="text-xs">{tab === "pending"
                    ? "Great — no pending KYC reviews. New submissions will appear here when AI cannot auto-decide."
                    : "Nothing to show right now."}</div>
                </td></tr>
              ) : (
                paged.visible.map((s) => {
                  const ai = s.ai || {};
                  const v = VERDICT_LABEL[(ai.verdict || "manual_review").toLowerCase()] || VERDICT_LABEL.manual_review;
                  return (
                    <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50/60" data-testid={`kyc-row-${s.id}`}>
                      <td className="px-4 py-3 text-xs text-gray-700 tabular whitespace-nowrap">{fmtDate(s.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{s.username || "—"}</div>
                        <div className="text-[11px] text-gray-500 font-mono">{s.id.slice(0, 8)}…</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold border ${v.cls}`}>
                          <Sparkles size={10} /> {v.txt}
                        </span>
                        {ai.reason && (
                          <div className="text-[11px] text-gray-500 mt-1 max-w-xs truncate" title={ai.reason}>{ai.reason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="flex items-center gap-3 tabular">
                          <span title="Face match">
                            <span className="text-gray-500">F</span> <b className={`font-mono ${
                              (ai.face_match_confidence ?? 0) >= 80 ? "text-emerald-700" :
                              (ai.face_match_confidence ?? 0) >= 60 ? "text-amber-700" : "text-rose-700"
                            }`}>{ai.face_match_confidence ?? 0}%</b>
                          </span>
                          <span title="Name match">
                            <span className="text-gray-500">N</span> <b className={`font-mono ${
                              (ai.name_match_confidence ?? 0) >= 80 ? "text-emerald-700" :
                              (ai.name_match_confidence ?? 0) >= 60 ? "text-amber-700" : "text-rose-700"
                            }`}>{ai.name_match_confidence ?? 0}%</b>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><Pill status={s.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setActive(s)}
                          className="h-8 px-3 rounded-sm bg-violet-500 hover:bg-violet-600 text-white text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid={`kyc-review-btn-${s.id}`}
                        >
                          <Eye size={12} /> Review
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="kyc-pg" />
      </div>

      {active && (
        <ReviewModal
          submission={active}
          onClose={() => setActive(null)}
          onDecided={(newStatus) => {
            // Optimistic update — set the row's status, then close.
            setItems((prev) => prev.map((it) =>
              it.id === active.id ? { ...it, status: newStatus, decision_at: new Date().toISOString() } : it
            ));
            setActive(null);
            // refresh count for current tab if it now belongs elsewhere
            if (tab !== "all" && newStatus !== tab) load(tab);
          }}
        />
      )}
    </div>
  );
}
